import { Component, OnInit, OnDestroy, ChangeDetectorRef, HostListener, ElementRef } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { NotificationService } from '@app/api/notification.service';
import { NotificationStateService, UserNotification } from '@app/services/notification-state.service';
import { SignalRService } from '@app/services/signalr.service';
import { ToastrService } from 'ngx-toastr';
import { environment } from '@app/../environments/environment';
import { Router } from '@angular/router';

@Component({
  selector: 'kt-notification-bell',
  templateUrl: './notification-bell.component.html',
  styleUrls: ['./notification-bell.component.scss'],
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  bellCount: number = 0;
  notifications: UserNotification[] = [];
  hasNew: boolean = false;
  isOpen: boolean = false;
  isLoading: boolean = false;
  host: string = environment.backendhost;

  private subscriptions: Subscription[] = [];
  private reloadSubject = new Subject<void>();

  constructor(
    private notificationService: NotificationService,
    private notificationStateService: NotificationStateService,
    private signalRService: SignalRService,
    private toastr: ToastrService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    // 1. Subscribe state changes
    this.subscriptions.push(
      this.notificationStateService.bellCount$.subscribe((count) => {
        this.bellCount = count;
        this.cdr.detectChanges();
      })
    );
    this.subscriptions.push(
      this.notificationStateService.notifications$.subscribe((list) => {
        this.notifications = list;
        this.cdr.detectChanges();
      })
    );
    this.subscriptions.push(
      this.notificationStateService.hasNew$.subscribe((has) => {
        this.hasNew = has;
        this.cdr.detectChanges();
      })
    );

    // 2. Debounce reload để tránh spam API khi nhiều event đến cùng lúc
    this.subscriptions.push(
      this.reloadSubject.pipe(debounceTime(300)).subscribe(() => {
        this.loadBell();
        this.loadNotifications();
      })
    );

    // 3. Subscribe SignalR event
    this.subscriptions.push(
      this.signalRService.notificationReceived$.subscribe((payload) => {
        // Hiển thị toast thông báo realtime
        if (payload && (payload.summary || payload.title || payload.details)) {
          const title = payload.summary || payload.title || 'Thông báo mới';
          const msg = payload.details || payload.message || '';
          this.toastr.info(msg, title, { timeOut: 5000, positionClass: 'toast-bottom-right' });
        }
        // Trigger reload bell + danh sách
        this.reloadSubject.next();
      })
    );

    // 4. Initial load
    this.reloadSubject.next();
  }

  /** Lấy số thông báo chưa đọc (chuông) - mục 3.5 */
  loadBell() {
    this.notificationService.apiUserNotificationBellGet().subscribe(
      (res) => {
        if (res && res.success) {
          // Response có thể là { data: number } hoặc { data: { count: number } }
          const count = typeof res.data === 'number' ? res.data : (res.data && res.data.count) || (res.data && res.data.total) || 0;
          this.notificationStateService.setBellCount(count);
        }
      },
      (err) => {
        console.error('[NotificationBell] loadBell error', err);
      }
    );
  }

  /** Lấy danh sách thông báo - mục 3.2 */
  loadNotifications() {
    this.isLoading = true;
    this.notificationService.apiUserNotificationGet(1, 10).subscribe(
      (res) => {
        this.isLoading = false;
        if (res && res.success && res.data) {
          const list: UserNotification[] = (res.data.collection || []).map((n: any) => ({
            id: n.id,
            full_name: n.full_name,
            details: n.details,
            reference_link: n.reference_link,
            summary: n.summary,
            created_at: n.created_at,
            read: n.read,
            group_code: n.group_code,
            sub_category_type: n.sub_category_type,
          }));
          this.notificationStateService.setNotifications(list);
        }
        this.cdr.detectChanges();
      },
      (err) => {
        this.isLoading = false;
        console.error('[NotificationBell] loadNotifications error', err);
      }
    );
  }

  /** Toggle dropdown */
  toggleDropdown(event: Event) {
    event.stopPropagation();
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      // Khi mở dropdown thì reset cờ "có mới"
      this.notificationStateService['hasNewSubject'].next(false);
    }
    this.cdr.detectChanges();
  }

  closeDropdown(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.isOpen = false;
    this.cdr.detectChanges();
  }

  /** Close dropdown when click outside */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.isOpen) {
      return;
    }
    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.closeDropdown();
    }
  }

  /** Click a notification → mark as read, close dropdown, and redirect to /myprofile#notifications */
  onNotificationClick(notification: UserNotification, event: Event) {
    event.stopPropagation();
    this.closeDropdown();
    if (!notification.read) {
      this.notificationService.apiNotificationIdReadPut(notification.id).subscribe(
        (res) => {
          if (res && res.success) {
            this.notificationStateService.markNotificationRead(notification.id);
          }
        },
        (err) => console.error('[NotificationBell] markRead error', err)
      );
    }
    this.router.navigate(['/myprofile'], { fragment: 'notifications' });
  }

  /** Mark all as read - mục 3.3 */
  markAllAsRead(event: Event) {
    event.stopPropagation();
    this.notificationService.apiNotificationMarkAllAsReadPut().subscribe(
      (res) => {
        if (res && res.success) {
          this.notificationStateService.markAllNotificationsRead();
          this.toastr.success('Đã đánh dấu tất cả thông báo là đã đọc');
        } else {
          this.toastr.error(res && res.message ? res.message : 'Đánh dấu thất bại');
        }
      },
      (err) => {
        this.toastr.error('Đánh dấu tất cả đã đọc thất bại');
        console.error('[NotificationBell] markAllAsRead error', err);
      }
    );
  }

  /** Format thời gian tương đối */
  formatTime(dateStr?: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Vừa xong';
    if (diffMin < 60) return `${diffMin} phút trước`;
    const diffHour = Math.floor(diffMin / 60);
    if (diffHour < 24) return `${diffHour} giờ trước`;
    const diffDay = Math.floor(diffHour / 24);
    if (diffDay < 30) return `${diffDay} ngày trước`;
    return date.toLocaleDateString('vi-VN');
  }

  /** Bypass Angular sanitizer to keep inline style (font-weight, font-style) from API */
  getSafeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html || '');
  }

  trackById(index: number, item: UserNotification): string {
    return item.id;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
